import { ChangeEvent, MouseEvent, useCallback, useRef, useState } from 'react'
import Swal from 'sweetalert2'
import withReactContent from 'sweetalert2-react-content'
import { useIntl } from 'react-intl'

import { MatchingFile } from '../../core/_models'
import {
  addBillMatchingPaymentTicket,
  rejectMatchingPaymentBill,
  replaceMatchingPaymentReceipt,
  verifyMatchingPaymentBill,
} from '../../core/_requests'
import { EFileStatus } from '../../../../../types/enums'
import { getErrorCode, getErrorText } from '../../../../common/api/errors'

const AppSwal = withReactContent(Swal)

const ALLOWED_MIME = new Set(['image/jpeg', 'image/png', 'application/pdf'])
const ALLOWED_EXT = new Set(['jpg', 'jpeg', 'png', 'pdf'])
const MAX_FILE_SIZE = 5 * 1024 * 1024

export const RECEIPT_ACCEPT = '.jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf'

// Решение по чеку, который другой сотрудник уже заменил: страница открыта до замены.
const DECISION_ERROR_LABELS: Record<string, string> = {
  RECEIPT_REPLACED: 'Чек уже заменён другим сотрудником. Обновите страницу.',
}

const REPLACE_ERROR_LABELS: Record<string, string> = {
  FILE_TOO_LARGE: 'Размер файла не должен превышать 5MB',
  VALIDATION_ERROR: 'Файл не подходит: для этого мерчанта принимается только PDF, для остальных — PDF, JPG или PNG',
  TICKET_VIRTUAL_MERCHANT: 'Чек пополнения виртуального мерчанта не заменяется',
  TICKET_STATE_INVALID: 'У пополнения нет чека — его нужно прикрепить, а не заменить',
}

/** Общая проверка файла чека перед отправкой; текст предупреждения или null. */
const validateReceiptFile = (file: File): string | null => {
  const mimeOk = ALLOWED_MIME.has(file.type)
  const ext = file.name.split('.').pop()?.toLowerCase()
  const extOk = ext && ALLOWED_EXT.has(ext)

  if (!(mimeOk || extOk)) {
    return 'Пожалуйста, выберите изображение'
  }

  if (file.size > MAX_FILE_SIZE) {
    return 'Размер файла не должен превышать 5MB'
  }

  return null
}

interface UseReceiptActionsArgs {
  ticketToken: string
  /** Право на операции с чеками; без него действия не выполняются. */
  canManageBills: boolean
  /** Чек сохранён — обновить строку локально, без полного рефетча списка. */
  onUploaded: (file: MatchingFile) => void
  /** Решение по чеку принято — обновить статус файла в строке. */
  onDecided: (billId: MatchingFile['id'], status: EFileStatus) => void
  /** Вызывается после показа итогового уведомления. Активная очередь убирает здесь тикет
   *  из списка: решение по чеку выводит его из очереди. Завершённые и журнал диспутов
   *  тикет оставляют — статус тикета решение по чеку там не меняет. */
  onDecisionAcknowledged?: () => void
  /** Чек заменён — все чеки тикета из ответа: текущий первым, затем история. */
  onReplaced?: (files: MatchingFile[]) => void
}

/**
 * Прикрепление и проверка чека депозита — один путь на все поверхности мэтчинга
 * (активная очередь, завершённые, журнал диспутов).
 *
 * Различия поверхностей вынесены в колбэки: сами запросы, валидация файла и тексты
 * везде одинаковы, иначе 409 или отказ проверки выглядели бы в разных списках по-разному.
 */
export const useReceiptActions = ({
  ticketToken,
  canManageBills,
  onUploaded,
  onDecided,
  onDecisionAcknowledged,
  onReplaced,
}: UseReceiptActionsArgs) => {
  const intl = useIntl()
  const [isUploading, setIsUploading] = useState(false)
  const [isReplacing, setIsReplacing] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const replaceInputRef = useRef<HTMLInputElement>(null)

  const handleUploadClick = useCallback((e: MouseEvent<HTMLButtonElement>) => {
    if (!canManageBills) {
      AppSwal.fire({ title: 'Предупреждение', text: 'У Вас нет прав прикреплять чеки', icon: 'warning' })
      return
    }
    fileInputRef.current?.click()
    e.currentTarget.blur()
  }, [canManageBills])

  const handleFileSelect = useCallback(async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    const problem = validateReceiptFile(file)
    if (problem) {
      AppSwal.fire({ title: 'Предупреждение', text: problem, icon: 'warning' })
      return
    }

    setIsUploading(true)
    try {
      const response = await addBillMatchingPaymentTicket(ticketToken, file)
      if (response.status === 200 && response.data && response.data.success) {
        onUploaded({
          id: response.data.mediaObject.id,
          status: response.data.mediaObject.status,
          url: response.data.mediaObject.url,
        } as MatchingFile)
        AppSwal.fire({
          title: 'Чек успешно загружен!',
          timer: 1500,
        })
      } else {
        AppSwal.fire({ title: 'Ошибка', text: 'Не удалось загрузить чек', icon: 'error' })
      }
    } catch (error: any) {
      if (error?.status === 409) {
        AppSwal.fire({ title: 'Ошибка', text: 'Файл уже загружен для этого тикета. Обновите страницу.', icon: 'error' })
      } else {
        AppSwal.fire({ title: 'Ошибка', text: getErrorText(error, 'Не удалось загрузить чек', { withFieldName: false }), icon: 'error' })
      }
    } finally {
      setIsUploading(false)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }, [ticketToken, onUploaded])

  const handleVerifyBill = useCallback(async (fileToVerify: MatchingFile) => {
    if (!canManageBills) return
    if (typeof fileToVerify?.id !== 'number' || !fileToVerify?.id) return
    const id = fileToVerify?.id?.toString()
    AppSwal.fire({
      title: (
        <>
          Подтвердить чек № {fileToVerify?.id}
          {!!fileToVerify?.token && <br />}
          {!!fileToVerify?.token && <small className='text-muted'>{fileToVerify?.token}</small>}
        </>
      ),
      showCancelButton: true,
      cancelButtonText: 'Нет',
      confirmButtonText: 'Да, подтвердить',
      showLoaderOnConfirm: true,
      preConfirm: async () => {
        try {
          const response = await verifyMatchingPaymentBill(id)
          if (response.status === 200) {
            onDecided(fileToVerify.id, EFileStatus.VERIFIED)
            return true
          }
          throw new Error(response.data?.message || response?.statusText || 'неизвестная ошибка')
        } catch (err) {
          const known = DECISION_ERROR_LABELS[getErrorCode(err) ?? '']
          if (known) {
            AppSwal.fire({ title: 'Ошибка', text: known, icon: 'error' })
            return false
          }
          const reason = err instanceof Error ? err.message : getErrorText(err, '', { withFieldName: false })
          AppSwal.fire({ title: 'Ошибка', text: 'Произошла ошибка при подтверждении чека' + (reason ? `: ${reason}` : ''), icon: 'error' })
          return false
        }
      },
      allowOutsideClick: () => !Swal.isLoading(),
    }).then(async (result) => {
      if (result.isConfirmed && result.value === true) {
        await AppSwal.fire({
          title: `${intl.formatMessage({ id: 'receipt.action.VERIFY' })}!`,
          timer: 1500,
        })
        onDecisionAcknowledged?.()
      }
    })
  }, [onDecided, onDecisionAcknowledged, intl, canManageBills])

  const handleRejectBill = useCallback(async (fileToReject: MatchingFile) => {
    if (!canManageBills) return
    if (typeof fileToReject?.id !== 'number' || !fileToReject?.id) return
    const id = fileToReject.id.toString()
    AppSwal.fire({
      title: (
        <>
          Отклонить чек № {fileToReject?.id}
          {!!fileToReject?.token && <br />}
          {!!fileToReject?.token && <small className='text-muted'>{fileToReject?.token}</small>}
        </>
      ),

      showCancelButton: true,
      cancelButtonText: 'Нет',
      confirmButtonText: 'Да, отклонить',
      showLoaderOnConfirm: true,
      preConfirm: async () => {
        try {
          const response = await rejectMatchingPaymentBill(id)
          if (response.status === 200) {
            onDecided(fileToReject.id, EFileStatus.REJECTED)
            return true
          }
          throw new Error(response.data?.message || response?.statusText || 'неизвестная ошибка')
        } catch (err) {
          const known = DECISION_ERROR_LABELS[getErrorCode(err) ?? '']
          if (known) {
            AppSwal.fire({ title: 'Ошибка', text: known, icon: 'error' })
            return false
          }
          const reason = err instanceof Error ? err.message : getErrorText(err, '', { withFieldName: false })
          AppSwal.fire({ title: 'Ошибка', text: 'Произошла ошибка при отклонении чека' + (reason ? `: ${reason}` : ''), icon: 'error' })
          return false
        }
      },
      allowOutsideClick: () => !Swal.isLoading(),
    }).then(async (result) => {
      if (result.isConfirmed && result.value === true) {
        await AppSwal.fire({
          title: `${intl.formatMessage({ id: 'receipt.action.REJECT' })}!`,
          timer: 1500,
        })
        onDecisionAcknowledged?.()
      }
    })
  }, [onDecided, onDecisionAcknowledged, intl, canManageBills])

  const handleReplaceClick = useCallback((e: MouseEvent<HTMLButtonElement>) => {
    if (!canManageBills) {
      AppSwal.fire({ title: 'Предупреждение', text: 'У Вас нет прав заменять чеки', icon: 'warning' })
      return
    }
    replaceInputRef.current?.click()
    e.currentTarget.blur()
  }, [canManageBills])

  const handleReplaceSelect = useCallback(async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    const problem = validateReceiptFile(file)
    if (problem) {
      AppSwal.fire({ title: 'Предупреждение', text: problem, icon: 'warning' })
      return
    }

    setIsReplacing(true)
    try {
      const response = await replaceMatchingPaymentReceipt(ticketToken, file)
      if (response.data.success) {
        onReplaced?.(response.data.data.files)
        AppSwal.fire({ title: 'Чек заменён', timer: 1500 })
      } else {
        AppSwal.fire({ title: 'Ошибка', text: getErrorText(response.data, 'Не удалось заменить чек', { labels: REPLACE_ERROR_LABELS, withFieldName: false }), icon: 'error' })
      }
    } catch (error) {
      AppSwal.fire({ title: 'Ошибка', text: getErrorText(error, 'Не удалось заменить чек', { labels: REPLACE_ERROR_LABELS, withFieldName: false }), icon: 'error' })
    } finally {
      setIsReplacing(false)
      if (replaceInputRef.current) {
        replaceInputRef.current.value = ''
      }
    }
  }, [ticketToken, onReplaced])

  return {
    fileInputRef,
    isUploading,
    handleUploadClick,
    handleFileSelect,
    handleVerifyBill,
    handleRejectBill,
    replaceInputRef,
    isReplacing,
    handleReplaceClick,
    handleReplaceSelect,
  }
}
